import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHrMe, useInbox } from "../../hooks/hr";
import { AdjustmentsPanel } from "./AdjustmentsPanel";
import { ApprovalsPanel } from "./ApprovalsPanel";
import { AttendancePanel } from "./AttendancePanel";
import { DevicesPanel } from "./DevicesPanel";
import { HrOverview, type HrTabId } from "./HrOverview";
import { LeavePanel } from "./LeavePanel";
import { LoansPanel } from "./LoansPanel";
import { MyHr } from "./MyHr";
import { PayrollPanel } from "./PayrollPanel";
import { PeoplePanel } from "./PeoplePanel";
import { SetupPanel } from "./SetupPanel";

interface Tab { id: HrTabId; icon: string }

/**
 * The HR module. What each person sees follows the database-derived access level (0 self-service, 1 manager, 2 HR, 3 payroll/owner):
 * the tabs below are only a convenience, row-level security is what actually guards the data.
 */
export function HrHub() {
  const { t } = useTranslation();
  const me = useHrMe();
  const level = me.data?.level ?? 0;
  const inbox = useInbox(!!me.data?.enabled);
  const manages = level >= 1 || !!me.data?.manages;
  const [tab, setTab] = useState<HrTabId>("overview");

  const tabs: Tab[] = [
    { id: "overview", icon: "🏠" },
    ...(me.data?.employee ? ([{ id: "my", icon: "🙋" }] as Tab[]) : []),
    ...(manages ? ([{ id: "attendance", icon: "🕘" }, { id: "leave", icon: "🌴" }, { id: "approvals", icon: "✅" }] as Tab[]) : []),
    ...(level >= 1 ? ([{ id: "people", icon: "👥" }, { id: "adjustments", icon: "🏅" }] as Tab[]) : []),
    ...(level >= 2 ? ([{ id: "loans", icon: "💳" }, { id: "payroll", icon: "💰" }, { id: "devices", icon: "🖐️" }, { id: "setup", icon: "⚙️" }] as Tab[]) : []),
  ];
  // a plain employee lands on their own page
  useEffect(() => { if (me.data && level === 0 && !manages && me.data.employee && tab === "overview") setTab("my"); }, [me.data, level, manages, tab]);

  if (me.isLoading) return <div className="text-sm font-bold text-gray-500">{t("common.loading")}</div>;
  if (!me.data?.enabled) return <div className="card p-6 text-sm font-bold text-gray-500">{t("hr.disabled")}</div>;
  const waiting = inbox.data?.length ?? 0;

  return (
    <div className="space-y-5">
      <div className="card flex flex-wrap items-center gap-3.5 p-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-cosmic-purple to-solar-orange text-2xl shadow-cosmic" aria-hidden>👥</div>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-black tracking-tight">{t("hr.title")}</h1>
          <p className="text-xs font-medium text-gray-500">{t("hr.subtitle")}</p>
        </div>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label={t("hr.title")}>
        {tabs.map((x) => (
          <button key={x.id} role="tab" data-tab={x.id} aria-selected={tab === x.id} type="button" onClick={() => setTab(x.id)}
            className={`flex flex-shrink-0 items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-black transition ${tab === x.id ? "bg-cosmic-purple text-white shadow-cosmic" : "bg-white text-gray-600 hover:bg-purple-50"}`}>
            <span aria-hidden>{x.icon}</span>{t(`hr.tabs.${x.id}`)}
            {x.id === "approvals" && waiting > 0 && <span className="num rounded-full bg-solar-orange px-1.5 text-[10px] text-white">{waiting}</span>}
          </button>
        ))}
      </div>
      {tab === "overview" && <HrOverview level={level} go={setTab} />}
      {tab === "my" && <MyHr me={me.data} />}
      {tab === "attendance" && <AttendancePanel level={level} />}
      {tab === "leave" && <LeavePanel level={level} />}
      {tab === "approvals" && <ApprovalsPanel />}
      {tab === "people" && <PeoplePanel level={level} />}
      {tab === "loans" && <LoansPanel level={level} />}
      {tab === "adjustments" && <AdjustmentsPanel level={level} />}
      {tab === "payroll" && <PayrollPanel level={level} />}
      {tab === "devices" && <DevicesPanel level={level} />}
      {tab === "setup" && <SetupPanel level={level} />}
    </div>
  );
}
